/*!
 * cam.js — cam(): the camera, on the stage and as signals
 * © 2026 Rob Canning — GPLv3
 *
 *     cam()                              the camera, the whole view, mirrored (as a selfie)
 *     cam(uid:eye, at:[0.75, 0.3], size:0.3)   in a corner (as image()'s at: and size:)
 *     cam(uid:eye, cell:5)                in a cell of the grid
 *     cam(flip:0)                        not mirrored; fit:contain the whole picture, bars round it
 *     cam(opacity:0.4)                   faint; opacity:follow(bass.level, 0.2, 1)
 *     cam(show:0)                        not drawn: its signals only
 *     cam(off)                           every camera off, and the camera let go
 *     cam(uid:eye, off:1)                 that one
 *     cam(blur:4, warp:20, hue:120)      a box's effects on the picture, as viz(eye, ...) gives them:
 *                                        hue invert contrast saturate bright blur pixel warp grain
 *                                        split -- a number or follow(); fx:off all of them
 *     cam(kaleido:6, turn:30)            the picture folded in its frame; mirror:x|y|xy, tile:3
 *                                        (one at a time, in that order; kaleido, turn, tile follow() too)
 *     screen(kaleido:6)                  the stage folded, the camera with it
 *
 * Signals, under its uid (cam unless named), each frame:
 *     cam.motion   the share of the picture moving, 0..1
 *     cam.level    the same against its own most lately, 0..1: as a sound's
 *                  level, so follow(cam.level, lo, hi) reaches hi in any room
 *     cam.bright   how bright it is, 0..1
 *     cam.x cam.y  where the movement is, 0..1 across and down (as shown: mirrored)
 *     cam.hit      a burst of movement -- trig:cam.hit, every(4, cam.hit), cam.n
 * (camMotion.js works them out, on a 32 x 24 copy of each frame.)
 *
 * In a score the cue's element is the frame, as image()'s: the picture fills
 * it (cover by default), the element hidden under it. From the console, a
 * frame on the stage, in the shapes' layer (dark mode keeps its colours).
 *
 * One camera for every cam(): asked for once. The browser asks the performer
 * first, and only after something they did (a click, a key): from a cue the
 * playhead fires at load it may be refused, as adc()'s microphone.
 */

import { scoreSvg, shapesGroup, placeFrame } from "./shape.js";
import { stageBand, atPair } from "../system/viewBand.js";
import { publish, isMoving, bindParam, releaseBinding } from "../control/paramBinding.js";
import { pulse } from "../control/liveSignals.js";
import { cellOf, cellPlacement, remember, forget } from "../system/stageGrid.js";
import { makeValueGenerator, evalMaybeRandom } from "./audio/audioShared.js";
import { lumaOf, compareFrames, hitState, detectMotionHit } from "../system/camMotion.js";
import { FILTER_KEYS } from "../system/boxFx.js";
import { maskFromArgs } from "../system/stageMask.js";
import { onReset } from "../system/teardown.js";
import { mapHtml, rebend, forgetHtml, onlyMap } from "../system/mapHandles.js";
import { mapText } from "../system/mapWarp.js";

const GEOM = ["kaleido", "turn", "mirror", "tile"];

const SVG_NS = "http://www.w3.org/2000/svg";
const A_W = 32, A_H = 24;               // the analysis copy
const ANALYSIS_MS = 33;                 // ... at most 30 a second
const MAX_PX = 1920;                    // a picture's canvas, at most this wide

let stream = null, video = null, starting = null, lastError = null;
const cams = new Map();                 // uid -> { frame, own, holder, fo, canvas, flip, fit, hidden }
const watched = new Map();              // uid -> { flip }: whose signals go out
let raf = 0, lastAnalysis = 0, analysis = null, prevLuma = null;
const last = { x: 0.5, y: 0.5 };
const hit = hitState();
let motionShown = 0, motionMost = 0;
const MOST_FALL = 0.997, MOST_FLOOR = 0.02;   // its most lately: falls by half in ~8 s; never under 2 %

const off = v => v === 0 || v === "0" || v === false || /^(off|none|false|no)$/i.test(String(v ?? ""));

// ---- the camera ----------------------------------------------------------------

/** Why the camera did not open, in plain words (null: it did). */
function camTrouble() {
  const e = lastError;
  if (!e) return null;
  if (!window.isSecureContext) return "the page is not secure (http on another machine): the browser gives no camera there -- open it over https, or on localhost";
  switch (e.name) {
    case "NotFoundError": return "no camera to open: the system offers none -- plug one in, or turn it on";
    case "NotAllowedError": return "the browser was refused the camera: allow it for this page (the icon at the left of the address bar)";
    case "NotReadableError": return "the camera is in use or will not start: another program may hold it";
    default: return `the camera did not open: ${e.message || e.name}`;
  }
}

async function startCamera() {
  if (stream?.active && video) return true;
  if (starting) return starting;
  starting = (async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error("no getUserMedia"), { name: window.isSecureContext ? "NotFoundError" : "SecurityError" });
      stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
      lastError = null;
      video = document.createElement("video");
      video.muted = true;
      video.playsInline = true;
      video.srcObject = stream;
      // in the page, out of sight: some browsers do not decode a video that is nowhere
      video.style.cssText = "position:fixed;left:0;top:0;width:2px;height:2px;opacity:0;pointer-events:none";
      document.body.appendChild(video);
      await video.play();
      return true;
    } catch (err) {
      lastError = err;
      stream = null;
      return false;
    } finally {
      starting = null;
    }
  })();
  return starting;
}

function stopCamera() {
  for (const t of stream?.getTracks?.() || []) t.stop();
  stream = null;
  video?.remove();
  video = null;
  cancelAnimationFrame(raf);
  raf = 0;
  prevLuma = null;
}

// ---- where it is drawn -------------------------------------------------------------

/**
 * A console line's frame: a rect on the stage, as image()'s -- at: 0..1 of
 * the view (the middle by default), size: a share of it across and down (1,
 * all of it; [w, h] apart). One for each uid.
 */
function stageFrame(uid, at, size, was) {
  const svg = scoreSvg();
  if (!svg) return null;
  const group = shapesGroup(svg);
  let rect = was?.own && was.frame?.isConnected ? was.frame : null;
  if (!rect) {
    rect = document.createElementNS(SVG_NS, "rect");
    rect.id = `cam-frame-${uid}`;
    rect.setAttribute("fill", "none");
    rect.style.pointerEvents = "none";
    group.appendChild(rect);
  }
  return placeFrame(rect, group, at, size) ? rect : null;
}

/**
 * What the frame shows: a canvas in a foreignObject, in a group of its own
 * after the frame -- tagged as a picture's holder, so viz(uid, ...) finds it
 * and puts a box's effects on it, and screen()'s copies paint it as they do
 * a scope's canvas.
 */
function holderFor(c, uid) {
  if (c.holder?.isConnected) return;
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("class", "oscilla-image-holder oscilla-cam");
  g.dataset.imageUid = uid;
  g.dataset.cam = "1";
  const fo = document.createElementNS(SVG_NS, "foreignObject");
  fo.style.pointerEvents = "none";
  const canvas = document.createElement("canvas");
  canvas.style.cssText = "width:100%;height:100%;display:block";
  fo.appendChild(canvas);
  g.appendChild(fo);
  c.frame.parentNode.insertBefore(g, c.frame.nextSibling);
  Object.assign(c, { holder: g, fo, canvas });
}

/** The foreignObject over its frame, and its canvas as many px as it covers. */
function fitToFrame(c) {
  let b;
  try { b = c.frame.getBBox(); } catch { return; }
  for (const [k, v] of [["x", b.x], ["y", b.y], ["width", b.width], ["height", b.height]]) {
    if (c.fo.getAttribute(k) !== String(v)) c.fo.setAttribute(k, v);
  }
  const r = c.frame.getBoundingClientRect(), dpr = Math.min(2, window.devicePixelRatio || 1);
  const k = Math.min(1, MAX_PX / Math.max(1, r.width * dpr));
  const W = Math.max(2, Math.round(r.width * dpr * k)), H = Math.max(2, Math.round(r.height * dpr * k));
  if (c.canvas.width !== W || c.canvas.height !== H) { c.canvas.width = W; c.canvas.height = H; }
}

function undraw(uid) {
  const c = cams.get(uid);
  if (!c) return;
  forgetHtml(`cam:${uid}`);
  c.holder?.remove();
  if (c.own) c.frame?.remove();
  else if (c.frame) c.frame.style.visibility = c.hidden ?? "";
  releaseBinding(`cam:${uid}:opacity`);
  for (const k of GEOM) releaseBinding(`cam:${uid}:${k}`);
  cams.delete(uid);
}

/** viz.js: a grid's shuffle or turn moved it (at: and size: shares of the view). */
export function moveCam(uid, at, size) {
  const c = cams.get(uid);
  if (!c?.own) return;
  stageFrame(uid, at, size, c);
  fitToFrame(c);
}

/** map: on the camera's picture: its canvas bent, its frame where map:1's corners start. */
function camMap(c, uid, v) {
  mapHtml(`cam:${uid}`, v, {
    flat: () => c.frame.getBoundingClientRect(),
    targets: () => (c.canvas ? [{ el: c.canvas }] : []),
    line: pts => `cam(uid:${uid}, map:${mapText(pts)})`,
    write: { uid, cues: ["cam"] },
  });
}

// ---- each frame ------------------------------------------------------------------

/** kaleido / mirror / tile asked of this one: which, or null (one at a time, in that order). */
function geomOf(c) {
  const k = Math.round(Number(c.geom?.kaleido) || 0), m = String(c.geom?.mirror ?? ""), t = Math.round(Number(c.geom?.tile) || 0);
  if (k >= 2) return { mode: "kaleido", n: k, turn: Number(c.geom.turn) || 0 };
  if (/[xy]/.test(m)) return { mode: "mirror", mx: m.includes("x"), my: m.includes("y") };
  if (t >= 2) return { mode: "tile", n: t };
  return null;
}

/** The picture `src` onto `g` (W x H): folded, mirrored or tiled about the frame's middle (as boxFx.js does a box). */
function compose(g, src, W, H, geo) {
  const cx = W / 2, cy = H / 2, R = 2 * Math.hypot(W, H);
  if (geo.mode === "kaleido") {
    const n = geo.n, half = Math.PI / n, turn = (geo.turn - 90) * Math.PI / 180;
    for (let k = 0; k < n; k++) {
      g.save();
      g.translate(cx, cy);
      g.rotate(k * 2 * Math.PI / n);
      // every other wedge mirrored across its axis: the edges meet
      if (k % 2) { g.rotate(turn); g.scale(1, -1); g.rotate(-turn); }
      g.beginPath();
      g.moveTo(0, 0);
      g.lineTo(R * Math.cos(turn - half), R * Math.sin(turn - half));
      g.lineTo(R * Math.cos(turn + half), R * Math.sin(turn + half));
      g.closePath();
      g.clip();
      g.drawImage(src, -cx, -cy);
      g.restore();
    }
  } else if (geo.mode === "mirror") {
    const flips = [[1, 1]];
    if (geo.mx) flips.push([-1, 1]);
    if (geo.my) flips.push([1, -1]);
    if (geo.mx && geo.my) flips.push([-1, -1]);
    for (const [fx, fy] of flips) {
      g.save();
      g.translate(cx, cy); g.scale(fx, fy); g.translate(-cx, -cy);
      g.beginPath();
      g.rect(-R, -R, geo.mx ? cx + R : 2 * R, geo.my ? cy + R : 2 * R);   // the left / top half
      g.clip();
      g.drawImage(src, 0, 0);
      g.restore();
    }
  } else {
    for (let i = 0; i < geo.n; i++) for (let j = 0; j < geo.n; j++) g.drawImage(src, i * W / geo.n, j * H / geo.n, W / geo.n, H / geo.n);
  }
}

function paint(c) {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh || !c.canvas) return;
  if (c.frame.isConnected === false) return;
  fitToFrame(c);
  const W = c.canvas.width, H = c.canvas.height;
  // folded, mirrored or tiled: drawn plain first, on a canvas of its own
  const geo = geomOf(c);
  let target = c.canvas;
  if (geo) {
    c.src ||= document.createElement("canvas");
    if (c.src.width !== W || c.src.height !== H) { c.src.width = W; c.src.height = H; }
    target = c.src;
  }
  const g = target.getContext("2d");
  // cover: the frame filled, the picture's overhang cut; contain: all of it, bars round it
  const s = c.fit === "contain" ? Math.min(W / vw, H / vh) : Math.max(W / vw, H / vh);
  const dw = vw * s, dh = vh * s;
  g.clearRect(0, 0, W, H);
  g.save();
  if (c.flip) { g.translate(W, 0); g.scale(-1, 1); }
  g.drawImage(video, (W - dw) / 2, (H - dh) / 2, dw, dh);
  g.restore();
  if (geo) {
    const out = c.canvas.getContext("2d");
    out.clearRect(0, 0, W, H);
    compose(out, c.src, W, H, geo);
  }
}

/**
 * A line's effects on its picture. The filters go as viz(uid, ...)'s (boxFx.js,
 * on the picture's holder); kaleido / turn / mirror / tile are drawn here. As
 * for viz(): a line naming any of them is the picture's effects -- one it no
 * longer names goes; a line naming none leaves them as they are.
 */
function camEffects(c, uid, ast) {
  const args = ast?.args || [];
  const named = args.filter(a => FILTER_KEYS.includes(a.type) || GEOM.includes(a.type) || a.type === "fx");
  if (!named.length) return;
  const off = v => /^(0|off|none)$/i.test(String(v));
  const allOff = args.some(a => a.type === "fx" && off(a.value));
  c.geom = {};
  for (const k of GEOM) {
    const owner = `cam:${uid}:${k}`;
    const a = allOff ? null : args.find(x => x.type === k);
    if (!a || off(a.value)) { releaseBinding(owner); continue; }
    if (isMoving(a.value) && k !== "mirror") bindParam(a.value, v => { c.geom[k] = v; }, { owner, min: -1e6, max: 1e6, default: 0 });
    else { releaseBinding(owner); c.geom[k] = a.value; }
  }
  const filters = args.filter(a => FILTER_KEYS.includes(a.type) || a.type === "fx");
  import("./viz.js").then(m => m.handleVizCue({ type: "cueViz", args: [{ type: "uid", value: uid }, ...filters] }));
}

function analyse(now) {
  if (!watched.size || now - lastAnalysis < ANALYSIS_MS) return;
  lastAnalysis = now;
  if (!analysis) {
    const cv = document.createElement("canvas");
    cv.width = A_W; cv.height = A_H;
    analysis = cv.getContext("2d", { willReadFrequently: true });
  }
  analysis.drawImage(video, 0, 0, A_W, A_H);
  const luma = lumaOf(analysis.getImageData(0, 0, A_W, A_H).data);
  const r = compareFrames(prevLuma, luma, A_W, { last });
  prevLuma = luma;
  if (r.motion > 0) { last.x = r.x; last.y = r.y; }
  // shown: up at once, down over about half a second -- a hand's wave is not a flicker
  motionShown = Math.max(r.motion, motionShown * 0.85);
  motionMost = Math.max(motionShown, motionMost * MOST_FALL, MOST_FLOOR);
  const level = Math.min(1, motionShown / motionMost);
  const burst = detectMotionHit(hit, r.motion, now);
  for (const [uid, w] of watched) {
    if (burst) pulse(uid, "cam");
    publish("cam", uid, { motion: motionShown, level, bright: r.bright, x: w.flip ? 1 - last.x : last.x, y: last.y });
  }
}

function frame(now) {
  raf = 0;
  if (!video || !stream?.active) return;
  if (video.readyState >= 2) {
    for (const c of cams.values()) paint(c);
    analyse(now);
  }
  raf = requestAnimationFrame(frame);
}

// ---- the cue -----------------------------------------------------------------------

/** Every camera off and the camera let go (a new example, a new session). */
export function stopAllCams() {
  for (const uid of [...cams.keys()]) undraw(uid);
  for (const uid of watched.keys()) forget(uid);
  watched.clear();
  stopCamera();
}
onReset("project", stopAllCams);    // the camera let go: the next project asks again if it wants it

export async function handleCamCue(ast, cueElement = null) {
  const p = {};
  for (const a of (ast?.args || [])) if (a?.type) p[a.type] = a.value;
  const uid = String(p.uid ?? "cam").trim() || "cam";

  if (p.off != null && !off(p.off)) {
    if (p.uid == null) { stopAllCams(); return true; }
    undraw(uid);
    forget(uid);
    watched.delete(uid);
    if (!watched.size) stopCamera();
    return true;
  }

  // map:[...] alone -- a fit dragged on the laptop, run by the projector: only that
  if (onlyMap(ast.args || [])) { const c = cams.get(uid); if (c) camMap(c, uid, p.map); return true; }

  const flip = p.flip == null ? true : !off(p.flip);
  watched.set(uid, { flip });

  if (p.show != null && off(p.show)) undraw(uid);
  else {
    const was = cams.get(uid);
    let frameEl = cueElement, own = false;
    if (!frameEl) {
      own = true;
      let at = atPair(p.at), size = p.size;
      if (p.cell != null) {
        // in a cell of the grid (stageGrid.js), as shape()'s and viz()'s
        let v = p.cell;
        if (v && typeof v === "object" && v.type === "funcCall") v = evalMaybeRandom(v);
        const { cell, why } = cellOf(v, uid, { makeGen: makeValueGenerator });
        if (cell == null) console.warn(`[cam] ${why}`);
        else {
          const entry = remember(uid, { cell, across: Number(p.across) || 1, down: Number(p.down) || 1, size: p.size, at: p.at }, p.cell);
          const band = stageBand();
          ({ at, size } = cellPlacement("image", entry, { width: band.width, height: band.height }));
        }
      } else if (p.at != null) forget(uid);
      frameEl = stageFrame(uid, at, size, was);
      if (!frameEl) return false;
    }
    if (was && was.frame !== frameEl) undraw(uid);
    const c = cams.get(uid) || { frame: frameEl, own };
    if (!own && c.hidden == null) { c.hidden = frameEl.style.visibility; frameEl.style.visibility = "hidden"; }
    c.flip = flip;
    c.fit = String(p.fit ?? "cover").toLowerCase() === "contain" ? "contain" : "cover";
    cams.set(uid, c);
    holderFor(c, uid);
    fitToFrame(c);
    // opacity: a number, or as a signal says
    const owner = `cam:${uid}:opacity`;
    if (isMoving(p.opacity)) bindParam(p.opacity, v => { c.holder.style.opacity = String(v); }, { owner, min: 0, max: 1, default: 1 });
    else { releaseBinding(owner); c.holder.style.opacity = p.opacity != null ? String(Math.max(0, Math.min(1, Number(p.opacity) || 0))) : ""; }
    // blur:, warp:, kaleido: ... (camEffects)
    camEffects(c, uid, ast);
    // mask:spot / hole:spot -- seen only through a shape, or with it cut out (stageMask.js)
    maskFromArgs(`cam:${uid}`, () => (c.canvas ? [c.canvas] : []), k => p[k]);
    // map:1 / map:[...] / map:0 -- the picture bent onto a surface (mapHandles.js)
    if (p.map != null) camMap(c, uid, p.map);
    else rebend(`cam:${uid}`);
  }

  const ok = await startCamera();
  if (!ok) {
    // said in the live console too: a prompt answered late comes back long after the line ran
    const text = `[cam] ${camTrouble() ?? "the camera did not open"}`;
    console.warn(text);
    dispatchEvent(new CustomEvent("oscilla:livecode-say", { detail: { text, kind: "warn" } }));
    return false;
  }
  if (!raf) raf = requestAnimationFrame(frame);
  return true;
}
